import { ToggleButton } from "@components/Layouts/Home/ToggleButton";
import Image from "@elements/ResponsiveImage/Image";
import Heading from "@elements/Typography/Heading";
import useWindowDimensions from "@hooks/useWindowDimensions";
import { MovieListingResponse } from "@typings/HomePagePayloadType";
import { useState } from "react";
import { twMerge } from "tailwind-merge";

type MovieTimingsSectionProps = {
  movies?: MovieListingResponse;
};

const MovieCard = ({
  title,
  url,
  imgUrl,
}: {
  title: string;
  url: string;
  imgUrl: string;
}) => {
  return (
    <a
      className={twMerge(
        "min-w-[120px] h-[242px] lg:min-w-[160px] lg:h-[300px]",
      )}
      href={url}
      target="_blank"
      rel="noreferrer"
    >
      <Image
        displayWidth={160}
        srcWidth={160}
        srcHeight={243}
        src={imgUrl}
        alt={twMerge("Movie: ", title)}
      />
      <p
        className={twMerge(
          "font-regular font-primary text-base lg:text-xs leading-300",
        )}
      >
        {title}
      </p>
    </a>
  );
};

const MOVIES_PER_SCROLL = 4;

export const MovieTimingsSection = ({ movies }: MovieTimingsSectionProps) => {
  const moviesList = movies?.result
    ? Object.entries(movies.result).map(([key, value]) => ({
        key,
        ...value,
      }))
    : [];
  const [currentMovieIndex, setCurrentMovieIndex] = useState(0);
  const { width } = useWindowDimensions();
  const isMobile = width <= 480;

  const onNextMovie = () => {
    const nextIndex = Math.min(
      currentMovieIndex + MOVIES_PER_SCROLL,
      moviesList.length - MOVIES_PER_SCROLL,
    );
    setCurrentMovieIndex(nextIndex);

    const scrollContainer = document.getElementById("movies-scroll-container");

    if (scrollContainer) {
      const cardWidth = scrollContainer.scrollWidth / moviesList.length;

      scrollContainer.scrollTo({
        left: nextIndex * cardWidth,
        behavior: "smooth",
      });
    }
  };

  const onPreviousMovie = () => {
    const prevIndex = Math.max(currentMovieIndex - MOVIES_PER_SCROLL, 0);

    setCurrentMovieIndex(prevIndex);

    const scrollContainer = document.getElementById("movies-scroll-container");

    if (scrollContainer) {
      const cardWidth = scrollContainer.scrollWidth / moviesList.length;

      scrollContainer.scrollTo({
        left: prevIndex * cardWidth,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="border-b border-pink-300">
      <div className="lg:my-md">
        <Heading
          Element="h2"
          className="lg:hidden text-gray-100 text-md font-bold hover:underline mb-5"
        >
          திரைப்பட நேரங்கள்
        </Heading>
        <div className="hidden lg:flex justify-between mb-md">
          <Heading Element="h2" className="text-gray-100 text-2.5md font-bold">
            திரைப்பட நேரங்கள்
          </Heading>
          <div className="flex gap-2.5 lg:pr-lg">
            <ToggleButton
              isPrevious
              isDisabled={currentMovieIndex === 0}
              onClick={onPreviousMovie}
            />
            <ToggleButton
              isDisabled={
                currentMovieIndex >= moviesList.length - MOVIES_PER_SCROLL
              }
              onClick={onNextMovie}
            />
          </div>
        </div>

        <div
          id="movies-scroll-container"
          className={twMerge(
            "flex overflow-y-hidden gap-sm lg:gap-2.5",
            isMobile ? "overflow-x-scroll" : "overflow-x-hidden",
          )}
        >
          {moviesList.map((movie, index) => (
            <MovieCard
              key={index}
              title={movie.title}
              url={movie.gv_url}
              imgUrl={movie.film_poster}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
